import React from 'react';
import { Fab, Badge, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChatIcon from '@material-ui/icons/Chat';

const useStyles = makeStyles((theme) => ({
    floatingButton: {
        position: 'fixed',
        bottom: theme.spacing(8),
        right: theme.spacing(3),
        zIndex: 888,
        '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: theme.shadows[8],
        },
        '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
        },
        transition: 'all 0.2s ease-in-out',
        boxShadow: theme.shadows[6],
        [theme.breakpoints.down('sm')]: {
            bottom: theme.spacing(2),
            right: theme.spacing(2),
            '& .MuiFab-sizeSmall': {
                width: 48,
                height: 48,
            },
        },
    },
    badge: {
        '& .MuiBadge-badge': {
            fontSize: '0.75rem',
            fontWeight: 'bold',
            minWidth: '20px',
            height: '20px',
            borderRadius: '10px',
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: theme.shadows[2],
        },
    },
}));

interface FloatingChatButtonProps {
    onClick: () => void;
    unreadCount?: number;
    disabled?: boolean;
}

const FloatingChatButton: React.FC<FloatingChatButtonProps> = ({
    onClick,
    unreadCount = 0,
    disabled = false,
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleClick = () => {
        if (!disabled) {
            onClick();
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
        }
    };

    return (
        <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            invisible={unreadCount === 0}
            className={classes.badge}
        >
            <Fab
                color="primary"
                aria-label={`Open chat ${unreadCount > 0 ? `(${unreadCount} unread messages)` : ''}`}
                title={`Open chat support ${unreadCount > 0 ? `- ${unreadCount} unread messages` : ''}`}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                className={classes.floatingButton}
                size={isMobile ? 'medium' : 'large'}
                tabIndex={0}
            >
                <ChatIcon />
            </Fab>
        </Badge>
    );
};

export default FloatingChatButton; 